import { useEffect, useState } from 'react';
import {
  Mail,
  Phone,
  MapPin,
  AlertCircle,
  ChevronDown,
  CalendarDays,
  Fingerprint,
  ShieldCheck,
  ShieldX,
  CircleUserRound,
  Settings2,
  Info,
  CheckCircle2,
  XCircle,
  Globe,
} from 'lucide-react';
import { Listbox } from '@headlessui/react';
import { realAPI } from '../api/realEndpoints';
import { formatDate } from '../utils/helpers';
import { getAvatarBackgroundColor, getUserImageSrc, getUserInitial } from '../utils/userImage';
import { useToastNotifications } from '../hooks/useToast';
import Modal from './Modal';
import '../pages/ContentManagement.css';

interface CustomerDetailsProps {
  customerId: string | number;
  isDeletedView?: boolean;
}

interface CustomerData {
  id?: string | number;
  name?: string;
  email?: string;
  mobileNumber?: string;
  countryCode?: string;
  status?: string;
  bio?: string;
  location?: string;
  emailVerified?: string | boolean;
  createdAt?: string;
  isDeleted?: string | boolean | number;
  image?: string;
  signInProvider?: string[] | string | null;
}

const statusOptions: Array<{ label: string; value: 'active' | 'inactive'; color: string }> = [
  { label: 'Active', value: 'active', color: 'text-blue-700' },
  { label: 'Inactive', value: 'inactive', color: 'text-rose-700' },
];

const formatProviderLabel = (provider: unknown): string => {
  if (typeof provider === 'string' && provider.trim()) {
    const value = provider.trim();
    return value.charAt(0).toUpperCase() + value.slice(1);
  }

  if (Array.isArray(provider) && provider.length > 0 && typeof provider[0] === 'string') {
    const value = provider[0].trim();
    if (value) {
      return value.charAt(0).toUpperCase() + value.slice(1);
    }
  }

  if (provider && typeof provider === 'object' && 'provider' in provider) {
    const nestedProvider = (provider as { provider?: unknown }).provider;
    if (typeof nestedProvider === 'string' && nestedProvider.trim()) {
      const value = nestedProvider.trim();
      return value.charAt(0).toUpperCase() + value.slice(1);
    }
  }

  return 'Email';
};

const isVerifiedEmail = (emailVerified?: string | boolean): boolean =>
  emailVerified === true || emailVerified === 'yes';

const isDeletedAccount = (flag?: string | boolean | number): boolean =>
  flag === true || flag === 'yes' || flag === 1;

const formatPhoneNumber = (mobileNumber?: string, countryCode?: string): string => {
  if (!mobileNumber || mobileNumber.trim() === '') return 'N/A';
  const code = countryCode?.trim() || '1';
  const cleanCode = code.startsWith('+') ? code.slice(1) : code;
  return `+${cleanCode}-${mobileNumber}`;
};

const getSafeErrorMessage = (err: unknown): string => {
  if (err instanceof Error && err.message) return err.message;
  return 'Something went wrong. Please try again.';
};

export default function CustomerDetails({ customerId, isDeletedView = false }: CustomerDetailsProps) {
  const { success, error: showError } = useToastNotifications();
  const [customer, setCustomer] = useState<CustomerData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isImagePreviewOpen, setIsImagePreviewOpen] = useState(false);
  const [editForm, setEditForm] = useState<{ name: string; status: 'active' | 'inactive' }>({
    name: '',
    status: 'active',
  });

  useEffect(() => {
    const fetchCustomer = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = (await realAPI.users.getUserDetails(customerId)) as CustomerData;
        setCustomer(data);
        setEditForm({
          name: data?.name || '',
          status: data?.status === 'inactive' ? 'inactive' : 'active',
        });
      } catch (err: unknown) {
        setError(getSafeErrorMessage(err));
      } finally {
        setIsLoading(false);
      }
    };

    if (customerId) {
      fetchCustomer();
    }
  }, [customerId]);

  const handleSaveChanges = async () => {
    const trimmedName = editForm.name.trim();
    if (!trimmedName) {
      showError('Customer name is required', 'Please enter a valid name');
      return;
    }
    if (trimmedName.length > 30) {
      showError('Customer name is too long', 'Name must be 30 characters or fewer');
      return;
    }
    if (!customer) return;

    const updates: { name?: string; status?: string } = {};
    if (trimmedName !== (customer.name || '')) {
      updates.name = trimmedName;
    }
    if (editForm.status !== (customer.status === 'inactive' ? 'inactive' : 'active')) {
      updates.status = editForm.status;
    }

    if (!updates.name && !updates.status) {
      setIsEditing(false);
      return;
    }

    setIsSaving(true);
    try {
      await realAPI.users.updateUser(customerId, updates);
      setCustomer((prev) =>
        prev
          ? {
              ...prev,
              ...updates,
            }
          : prev
      );
      setIsEditing(false);

      const changedName = Boolean(updates.name);
      const changedStatus = Boolean(updates.status);
      const message =
        changedName && changedStatus
          ? 'Name and status were updated successfully'
          : changedName
            ? 'Name was updated successfully'
            : 'Status was updated successfully';
      success('Customer updated', message);
    } catch (err: unknown) {
      showError('Failed to update customer', getSafeErrorMessage(err));
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditForm({
      name: customer?.name || '',
      status: customer?.status === 'inactive' ? 'inactive' : 'active',
    });
    setIsEditing(false);
  };

  if (isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="text-center">
          <div className="loader mx-auto"></div>
          <p className="mt-3 text-sm text-slate-500">Loading customer profile...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="max-w-md rounded-xl border border-red-200 bg-red-50 p-6 shadow-sm">
          <div className="mb-2 flex items-center gap-3">
            <AlertCircle className="h-6 w-6 text-red-600" />
            <h3 className="text-lg font-semibold text-red-900">Error loading customer</h3>
          </div>
          <p className="text-red-700">{error}</p>
        </div>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <p className="text-slate-600">No customer data found.</p>
      </div>
    );
  }

  const customerImageSrc = getUserImageSrc(customer.image, customer.signInProvider);
  const deletedProfile = isDeletedView || isDeletedAccount(customer.isDeleted);
  const emailVerified = isVerifiedEmail(customer.emailVerified);
  const displayPhone = formatPhoneNumber(customer.mobileNumber, customer.countryCode);
  const displayStatus = customer.status === 'inactive' ? 'inactive' : 'active';

  return (
    <div className="bg-blue-50/40 px-4 py-2 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-6xl space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 sm:text-3xl">Customer Profile</h1>
          </div>

          {!deletedProfile && (
            <div className="flex items-center gap-2">
              {isEditing ? (
                <>
                  <button
                    onClick={handleCancelEdit}
                    className="rounded-lg border border-blue-200 bg-white px-4 py-2 text-sm font-medium text-blue-700 transition hover:bg-blue-50"
                    disabled={isSaving}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveChanges}
                    className="rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-2 text-sm font-medium text-white transition hover:from-blue-700 hover:to-indigo-700 disabled:opacity-70"
                    disabled={isSaving}
                  >
                    {isSaving ? 'Saving...' : 'Save Changes'}
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setIsEditing(true)}
                  className="rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:from-blue-700 hover:to-indigo-700"
                >
                  Edit Customer
                </button>
              )}
            </div>
          )}
        </div>

        <div className="overflow-hidden rounded-2xl border border-blue-100 bg-white shadow-sm">
          <div className="bg-gradient-to-r from-slate-900 via-indigo-900 to-blue-900 px-5 py-5 text-white">
            <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-4">
                {customerImageSrc ? (
                  <img
                    src={customerImageSrc}
                    alt={customer.name || 'Customer'}
                    className="h-20 w-20 cursor-pointer rounded-full border-4 border-white/30 object-cover shadow-lg sm:h-24 sm:w-24"
                    onClick={() => setIsImagePreviewOpen(true)}
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.onerror = null;
                      target.src = '/logo192.png';
                    }}
                  />
                ) : (
                  <div
                    className="flex h-20 w-20 items-center justify-center rounded-full border-4 border-white/30 text-3xl font-bold text-white shadow-lg sm:h-24 sm:w-24"
                    style={{ backgroundColor: getAvatarBackgroundColor(customer.name) }}
                  >
                    {getUserInitial(customer.name, 'U')}
                  </div>
                )}

                <div>
                  {isEditing ? (
                    <div className="space-y-2">
                      <label className="block text-xs uppercase tracking-wide text-slate-300">
                        Customer Name
                      </label>
                      <input
                        type="text"
                        value={editForm.name}
                        onChange={(e) =>
                          setEditForm((prev) => ({
                            ...prev,
                            name: e.target.value,
                          }))
                        }
                        maxLength={30}
                        className="w-full max-w-sm rounded-md border border-white/40 bg-white/95 px-3 py-2 text-sm font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-300"
                      />
                      <p className="text-xs text-slate-300">{editForm.name.length}/30</p>
                    </div>
                  ) : (
                    <>
                      <h2 className="text-2xl font-bold">{customer.name || 'Unknown Customer'}</h2>
                      <p className="mt-1 text-sm text-slate-200">{customer.email || 'No email'}</p>
                    </>
                  )}
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                {deletedProfile && (
                  <span className="inline-flex items-center rounded-full bg-amber-100 px-3 py-1 text-sm font-semibold text-amber-800">
                    Deleted Account
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 p-4 lg:grid-cols-2">
            <section className="rounded-xl border border-blue-100 p-4">
              <h3 className="mb-4 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-blue-700">
                <span className="rounded-md bg-blue-100 p-1.5 text-blue-700">
                  <CircleUserRound className="h-4 w-4" />
                </span>
                Contact Information
              </h3>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="rounded-lg bg-blue-50/60 p-3">
                  <p className="mb-1 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    <Mail className="h-4 w-4 text-sky-500" />
                    Email
                  </p>
                  <p className="break-all text-sm font-medium text-slate-900">
                    {customer.email || 'N/A'}
                  </p>
                </div>

                <div className="rounded-lg bg-blue-50/60 p-3">
                  <p className="mb-1 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    <Phone className="h-4 w-4 text-indigo-500" />
                    Phone
                  </p>
                  <p className="text-sm font-medium text-slate-900">{displayPhone}</p>
                </div>

                <div className="rounded-lg bg-blue-50/60 p-3 sm:col-span-2">
                  <p className="mb-1 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    <MapPin className="h-4 w-4 text-rose-500" />
                    Location
                  </p>
                  <p className="text-sm font-medium text-slate-900">{customer.location || 'N/A'}</p>
                </div>
              </div>
            </section>

            <section className="rounded-xl border border-blue-100 p-4">
              <h3 className="mb-4 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-blue-700">
                <span className="rounded-md bg-blue-100 p-1.5 text-blue-700">
                  <Settings2 className="h-4 w-4" />
                </span>
                Account Controls
              </h3>

              <div className="space-y-3">
                <div className="rounded-lg border border-blue-100 px-3 py-2">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Account Status
                  </p>
                  {isEditing ? (
                    <div className="relative mt-2">
                      <Listbox
                        value={editForm.status}
                        onChange={(value) =>
                          setEditForm((prev) => ({
                            ...prev,
                            status: value as 'active' | 'inactive',
                          }))
                        }
                      >
                        <Listbox.Button className="w-full rounded-md border border-blue-200 bg-white px-3 py-2 pr-9 text-left text-sm font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500">
                          <span className="block truncate capitalize">{editForm.status}</span>
                          <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                        </Listbox.Button>
                        <Listbox.Options className="absolute z-20 mt-1 w-full rounded-md border border-blue-100 bg-white shadow-lg">
                          {statusOptions.map((option) => (
                            <Listbox.Option
                              key={option.value}
                              value={option.value}
                              className={({ active }) =>
                                `cursor-pointer px-3 py-2 text-sm ${active ? 'bg-slate-100' : ''}`
                              }
                            >
                              <span className={`font-medium ${option.color}`}>{option.label}</span>
                            </Listbox.Option>
                          ))}
                        </Listbox.Options>
                      </Listbox>
                    </div>
                  ) : (
                    <span
                      className={`mt-2 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold capitalize ${
                        displayStatus === 'active'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-rose-100 text-rose-800'
                      }`}
                    >
                      {displayStatus === 'active' ? (
                        <CheckCircle2 className="h-3.5 w-3.5" />
                      ) : (
                        <XCircle className="h-3.5 w-3.5" />
                      )}
                      {displayStatus}
                    </span>
                  )}
                </div>

                <div className="rounded-lg border border-blue-100 px-3 py-2">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Email Verification
                  </p>
                  <p className="mt-2 inline-flex items-center gap-2 text-sm font-medium text-slate-800">
                    {emailVerified ? (
                      <ShieldCheck className="h-4 w-4 text-blue-600" />
                    ) : (
                      <ShieldX className="h-4 w-4 text-amber-600" />
                    )}
                    {emailVerified ? 'Verified' : 'Not Verified'}
                  </p>
                </div>
              </div>
            </section>

            <section className="rounded-xl border border-blue-100 p-4">
              <h3 className="mb-4 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-blue-700">
                <span className="rounded-md bg-blue-100 p-1.5 text-blue-700">
                  <Fingerprint className="h-4 w-4" />
                </span>
                System Info
              </h3>

              <div className="space-y-3 text-sm">
                <div className="rounded-lg bg-blue-50/60 px-3 py-2">
                  <p className="mb-1 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    <CalendarDays className="h-4 w-4 text-violet-500" />
                    Member Since
                  </p>
                  <p className="font-medium text-slate-900">
                    {customer.createdAt ? formatDate(customer.createdAt) : 'Unknown'}
                  </p>
                </div>

                <div className="rounded-lg bg-blue-50/60 px-3 py-2">
                  <p className="mb-1 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    <Fingerprint className="h-4 w-4 text-amber-500" />
                    User ID
                  </p>
                  <p className="break-all font-mono text-xs text-slate-800">
                    {customer.id || 'N/A'}
                  </p>
                </div>

                <div className="rounded-lg bg-blue-50/60 px-3 py-2">
                  <p className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    <Globe className="h-4 w-4 text-cyan-600" />
                    Sign-in Method
                  </p>
                  <p className="mt-1 font-medium text-slate-900">
                    {formatProviderLabel(customer.signInProvider)}
                  </p>
                </div>
              </div>
            </section>

            <section className="rounded-xl border border-blue-100 p-4">
              <h3 className="mb-4 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-blue-700">
                <span className="rounded-md bg-blue-100 p-1.5 text-blue-700">
                  <Info className="h-4 w-4" />
                </span>
                Profile Summary
              </h3>
              <p className="text-sm leading-6 text-slate-700">
                {customer.bio?.trim()
                  ? customer.bio
                  : 'No bio available for this customer account.'}
              </p>
            </section>
          </div>
        </div>
      </div>

      <Modal
        isOpen={isImagePreviewOpen}
        onClose={() => setIsImagePreviewOpen(false)}
        title="Image Preview"
        size="lg"
      >
        {customerImageSrc ? (
          <img
            src={customerImageSrc}
            alt={customer.name || 'Customer'}
            className="max-h-[70vh] w-full rounded-lg object-contain"
          />
        ) : null}
      </Modal>
    </div>
  );
}
